// Live board — canon port (LiveBoardScreen).
// Per docs/UI_SPEC.md §5: scroll allowed on the bottom region only.
// Layout: TopBar (back/title/share) → bigger interactive StumbleMap with tappable
// pins + a peek card → 3-tab nav (Leaders / Proof / Squad) → scrollable region
// that switches per tab.
//
// Public/private split (per CLAUDE.md §7 + ADR — "private spots: city-only"):
//   - "ON THE MAP" → friends sharing exact location (visibility ∈ {friends, invite_only}
//                     AND has a currentVenue). They pin on the map and rank.
//   - "PRIVATE · CITY ONLY" → friends at houses or with private visibility.
//                     City label only, no pin, no rank badge.
//
// The /api/stumbling response does NOT expose venue lat/lng yet — we derive a
// stable in-canvas position from a hash of (nightId + venueId) so pins don't
// jump between polls. When the API gains lat/lng (Pass H/I) the position
// derivation can swap to real coords without touching layout.
//
// Pass G uses 10s REST polling; Pass I lights up Supabase Realtime.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type ActiveStumbleDto,
  type ProofItemDto,
  SELFIE_SAVED_EVENT,
  meApi,
  stumblingApi,
} from '../api'
import { Icon } from '../components/Icon'
import { StumbleMap } from '../components/StumbleMap'
import { BackButton, TopBar } from '../components/TopBar'

export const Route = createFileRoute('/stumbling')({
  component: StumblingRoute,
})

type Tab = 'board' | 'proof' | 'squad'

// Canon avatar palette — same ordering the rest of the app uses.
const AVATAR_COLORS = [
  'var(--pink)',
  'var(--lime)',
  'var(--teal)',
  'var(--orange)',
  'var(--plum)',
  'var(--acid)',
]

function StumblingRoute() {
  const navigate = useNavigate()
  const [stumbles, setStumbles] = useState<ActiveStumbleDto[]>([])
  const [proofs, setProofs] = useState<ProofItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('board')
  const [picked, setPicked] = useState<string | null>(null)
  const [activeNightId, setActiveNightId] = useState<string | null>(null)
  // Force re-render every 30s so "time ago" labels stay fresh.
  const [, tickRerender] = useState(0)
  const timerRef = useRef<number | null>(null)

  const refreshProofs = useCallback(async () => {
    const data = await stumblingApi.proof()
    setProofs(data)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const [live, proof, active] = await Promise.all([
        stumblingApi.live(),
        stumblingApi.proof(),
        meApi.activeNight(),
      ])
      setStumbles(live)
      setProofs(proof)
      setActiveNightId(active?.night.id ?? null)
    } finally {
      setLoading(false)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only polling bootstrap; refresh/tickRerender are stable closures.
  useEffect(() => {
    void refresh()
    timerRef.current = window.setInterval(refresh, 10_000)
    const tickTimer = window.setInterval(() => tickRerender((n) => n + 1), 30_000)
    // Live-board refetches the proof feed when any client posts a selfie.
    // Same-tab events only — cross-tab parity arrives with Supabase Realtime (Pass I).
    const onSelfie = () => {
      void refreshProofs()
    }
    window.addEventListener(SELFIE_SAVED_EVENT, onSelfie)
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current)
      window.clearInterval(tickTimer)
      window.removeEventListener(SELFIE_SAVED_EVENT, onSelfie)
    }
  }, [])

  // Split into public/sharing vs private/city-only buckets.
  const { sharing, anon } = useMemo(() => splitVisibility(stumbles), [stumbles])

  // F-wave PR γ (F10): active stumbles rank first, warm-ended (status='ended'
  // within the 1hr post-end window) sit below them. Within each bucket the
  // existing drink-count ranking applies. Sorting on the same array keeps the
  // map pins + leaders rows aligned.
  const sharingRanked = useMemo(
    () =>
      [...sharing].sort((a, b) => {
        const aWarm = a.status === 'ended' ? 1 : 0
        const bWarm = b.status === 'ended' ? 1 : 0
        if (aWarm !== bWarm) return aWarm - bWarm
        return b.drinkCount - a.drinkCount
      }),
    [sharing],
  )

  // Map positions are deterministic per nightId + venueId so they don't jump
  // between polls. Will swap to real lat/lng once the stumbling endpoint
  // surfaces venue coordinates.
  const positionedSharing = useMemo(
    () =>
      sharing.map((s, i) => ({
        stumble: s,
        pos: pseudoPosition(`${s.nightId}:${s.currentVenue?.id ?? ''}`),
        color: AVATAR_COLORS[i % AVATAR_COLORS.length],
        initial: initialOf(s.ownerDisplayName),
      })),
    [sharing],
  )

  const pickedRow = picked ? positionedSharing.find((p) => p.stumble.ownerId === picked) : null

  return (
    <div className="stumble-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/' })} />}
        eyebrow="right now"
        title="who's out"
        right={
          <button
            type="button"
            className="icon-btn"
            aria-label="Share your stumble"
            onClick={() => {
              if (typeof navigator !== 'undefined' && navigator.share) {
                void navigator
                  .share({ title: 'stumble', text: "who's out tonight", url: window.location.href })
                  .catch(() => {})
              }
            }}
          >
            <Icon name="share" size={18} />
          </button>
        }
      />

      <div className="screen-body with-nav" style={{ gridTemplateRows: 'auto auto 1fr', gap: 10 }}>
        {/* Interactive map with tappable friend pins */}
        <div style={{ position: 'relative' }}>
          <StumbleMap variant="treasure" height={210} you={{ x: 38, y: 46 }} pins={[]} trail={[]} />

          {/* Overlay tappable pins (the StumbleMap pins are presentational; we
              need buttons here for tap+highlight semantics). */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {positionedSharing.map((p) => {
              const active = picked === p.stumble.ownerId
              const dimmed = picked !== null && !active
              return (
                <button
                  key={p.stumble.ownerId}
                  type="button"
                  data-testid={`map-pin-${p.stumble.ownerId}`}
                  onClick={() => setPicked(active ? null : p.stumble.ownerId)}
                  aria-label={`${p.stumble.ownerDisplayName} on the map`}
                  style={{
                    position: 'absolute',
                    left: `${p.pos.x}%`,
                    top: `${p.pos.y}%`,
                    transform: 'translate(-50%, -100%)',
                    pointerEvents: 'auto',
                    width: active ? 56 : 44,
                    height: active ? 70 : 54,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 180ms cubic-bezier(.2,1.3,.4,1)',
                    filter: dimmed ? 'grayscale(0.35) opacity(0.55)' : 'none',
                    zIndex: active ? 20 : 10,
                  }}
                >
                  <svg viewBox="0 0 44 54" width="100%" height="100%" aria-hidden="true">
                    <title>Map pin</title>
                    <path
                      d="M22 2c-9 0-16 6.5-16 15.5C6 28 22 50 22 50s16-22 16-32.5C38 8.5 31 2 22 2z"
                      fill={p.color}
                      stroke="var(--line)"
                      strokeWidth="2.5"
                      filter="drop-shadow(0 3px 0 var(--shadow-ink))"
                    />
                    <circle
                      cx="22"
                      cy="18"
                      r="9"
                      fill="var(--paper)"
                      stroke="var(--line)"
                      strokeWidth="2"
                    />
                    <text
                      x="22"
                      y="22"
                      textAnchor="middle"
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 13,
                        fill: 'var(--ink)',
                      }}
                    >
                      {p.initial}
                    </text>
                  </svg>
                </button>
              )
            })}
          </div>

          {/* Peek card on pin tap — animates in from above the map */}
          {pickedRow && (
            <div className="map-peek" aria-live="polite" aria-label="Friend on the map">
              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 999,
                    background: pickedRow.color,
                    color: 'var(--paper)',
                    border: '2px solid var(--line)',
                    display: 'grid',
                    placeItems: 'center',
                    fontFamily: 'var(--font-display)',
                    fontSize: 16,
                  }}
                >
                  {pickedRow.initial}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, lineHeight: 1 }}>
                    {pickedRow.stumble.ownerDisplayName}
                  </div>
                  <div className="body-sm muted" style={{ fontSize: 11 }}>
                    {pickedRow.stumble.currentVenue
                      ? `at ${pickedRow.stumble.currentVenue.name}`
                      : 'between bars'}{' '}
                    · {pickedRow.stumble.drinkCount} deep
                  </div>
                </div>
                <button
                  type="button"
                  className="chip"
                  style={{ minHeight: 32, padding: '6px 10px', fontSize: 11 }}
                  onClick={() => {
                    // Placeholder — DM/wave is post-MVP.
                  }}
                >
                  wave
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setPicked(null)}
                  aria-label="Close peek"
                  style={{ width: 32, height: 32 }}
                >
                  <Icon name="close" size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Status pill: counts both buckets so the user knows there's a private group */}
          <div
            style={{
              position: 'absolute',
              left: 10,
              bottom: 10,
              padding: '6px 10px',
              border: '2px solid var(--line)',
              borderRadius: 999,
              background: 'var(--paper)',
              fontFamily: 'var(--font-stamp)',
              fontSize: 11,
              letterSpacing: '0.08em',
            }}
          >
            {sharing.length} on map · {anon.length} private
          </div>
        </div>

        {/* 3-tab nav */}
        <div className="row" role="tablist" aria-label="Live board sections" style={{ gap: 6 }}>
          {(
            [
              ['board', 'Leaders'],
              ['proof', 'Proof'],
              ['squad', 'Squad'],
            ] satisfies [Tab, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className="chip"
              style={{
                flex: 1,
                justifyContent: 'center',
                minHeight: 42,
                background: tab === id ? 'var(--ink)' : 'var(--paper)',
                color: tab === id ? 'var(--paper)' : 'var(--ink)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab content (the only scrollable region on the screen) */}
        <div className="scroll-region" style={{ display: 'grid', gap: 8 }}>
          {tab === 'board' && (
            <LeadersTab
              loading={loading}
              sharingRanked={sharingRanked}
              anon={anon}
              picked={picked}
              setPicked={setPicked}
              startStumble={() => navigate({ to: '/' })}
            />
          )}
          {tab === 'proof' && (
            <ProofTab
              proofs={proofs}
              loading={loading}
              activeNightId={activeNightId}
              onOpenCamera={() => {
                if (activeNightId) {
                  navigate({ to: '/night/$id', params: { id: activeNightId } })
                } else {
                  navigate({ to: '/' })
                }
              }}
            />
          )}
          {tab === 'squad' && <SquadTab />}
        </div>
      </div>

      {/* Canon peek-in keyframe — scoped here so the route is self-contained. */}
      <style>{`
        .map-peek {
          position: absolute;
          left: 10px;
          right: 10px;
          top: 10px;
          padding: 10px 12px;
          border: 2.5px solid var(--line);
          border-radius: 12px;
          background: var(--paper);
          box-shadow: 0 5px 0 var(--shadow-ink);
          z-index: 30;
          animation: peek-in 220ms cubic-bezier(.2,1.2,.4,1);
        }
        @keyframes peek-in {
          from { transform: translateY(-6px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .map-peek { animation: none; }
        }
      `}</style>
    </div>
  )
}

// ── Leaders tab ──────────────────────────────────────────────────────────

type PositionedRow = ActiveStumbleDto & { color: string; initial: string }

function LeadersTab({
  loading,
  sharingRanked,
  anon,
  picked,
  setPicked,
  startStumble,
}: {
  loading: boolean
  sharingRanked: ActiveStumbleDto[]
  anon: ActiveStumbleDto[]
  picked: string | null
  setPicked: (id: string | null) => void
  startStumble: () => void
}) {
  if (loading && sharingRanked.length === 0 && anon.length === 0) {
    return (
      <div className="card body-sm muted" style={{ textAlign: 'center' }}>
        loading…
      </div>
    )
  }

  if (!loading && sharingRanked.length === 0 && anon.length === 0) {
    const shareStumble = () => {
      const url =
        typeof window !== 'undefined' ? window.location.origin : 'https://staging.stmbl.party'
      const text = "we're out tonight — track along here"
      if (typeof navigator !== 'undefined' && navigator.share) {
        void navigator.share({ title: 'stumble', text, url }).catch(() => {})
      }
    }
    return (
      <div
        className="card"
        data-testid="leaders-empty"
        style={{ textAlign: 'center', padding: 22, display: 'grid', gap: 10 }}
      >
        <p className="display" style={{ fontSize: 32, margin: 0, color: 'var(--ink)' }}>
          It's quiet out there.
        </p>
        <p className="body-sm muted" style={{ fontSize: 14, margin: 0 }}>
          Send a stumble link to start the night.
        </p>
        <button
          type="button"
          className="btn pink full"
          style={{ marginTop: 6 }}
          onClick={shareStumble}
        >
          <Icon name="share" size={16} /> SHARE STUMBLE
        </button>
        <button type="button" className="chip" style={{ margin: '0 auto' }} onClick={startStumble}>
          or start one yourself
        </button>
      </div>
    )
  }

  return (
    <>
      {sharingRanked.length > 0 && (
        <>
          <div className="eyebrow" style={{ paddingLeft: 4, fontSize: 10 }}>
            on the map
          </div>
          {sharingRanked.map((s, i) => {
            const color = AVATAR_COLORS[i % AVATAR_COLORS.length]
            const active = picked === s.ownerId
            // F10 warm-ended: gray ghost styling so users see "still here" but
            // not "currently active". `isWarmEnded` is derived from the API's
            // status field; endedAt is checked to be defensive against
            // mis-serialized payloads (the API contract guarantees both).
            const isWarmEnded = s.status === 'ended' && s.endedAt != null
            // Top-3 ranking: #1 lime, #2/#3 paper, the rest plain paper rows.
            // Warm-ended rows skip the lime accent regardless of position and
            // wash out to the faded ink tone.
            const rankBg = active
              ? color
              : isWarmEnded
                ? 'var(--paper-warm)'
                : i === 0
                  ? 'var(--lime)'
                  : 'var(--paper)'
            const rankFg = active ? 'var(--paper)' : 'var(--ink)'
            return (
              <button
                key={s.nightId}
                type="button"
                data-testid={`live-stumble-${s.ownerId}`}
                data-warm={isWarmEnded ? 'true' : undefined}
                onClick={() => setPicked(active ? null : s.ownerId)}
                className="row-between"
                style={{
                  padding: '10px 14px',
                  border: '2.5px solid var(--line)',
                  borderRadius: 14,
                  background: rankBg,
                  color: rankFg,
                  boxShadow: '0 4px 0 var(--shadow-ink)',
                  textAlign: 'left',
                  minHeight: 44,
                  opacity: isWarmEnded && !active ? 0.7 : 1,
                }}
              >
                <div className="row" style={{ gap: 10 }}>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      background: isWarmEnded ? 'var(--paper)' : 'var(--ink)',
                      color: isWarmEnded ? 'var(--ink)' : 'var(--paper)',
                      border: '2px dashed var(--line)',
                      display: 'grid',
                      placeItems: 'center',
                      fontFamily: 'var(--font-display)',
                      fontSize: 16,
                      ...(isWarmEnded ? { borderStyle: 'dashed' } : { borderStyle: 'solid' }),
                    }}
                  >
                    {i + 1}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 20,
                        lineHeight: 1,
                        textTransform: 'lowercase',
                      }}
                    >
                      {s.ownerDisplayName}
                    </div>
                    <div className="body-sm" style={{ fontSize: 12, opacity: 0.85 }}>
                      {isWarmEnded
                        ? `ended ${formatEndedAgo(s.endedAt ?? new Date().toISOString())}`
                        : s.currentVenue
                          ? `at ${s.currentVenue.name}`
                          : 'between bars'}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontSize: 20, fontWeight: 800 }}>
                    {s.drinkCount}
                  </div>
                  <div className="eyebrow" style={{ fontSize: 9 }}>
                    {isWarmEnded ? 'wrapped' : 'deep'}
                  </div>
                </div>
              </button>
            )
          })}
        </>
      )}

      {anon.length > 0 && (
        <>
          <div className="eyebrow" style={{ paddingLeft: 4, fontSize: 10, marginTop: 4 }}>
            private · city only
          </div>
          {anon.map((s, i) => {
            const color = AVATAR_COLORS[(i + sharingRanked.length) % AVATAR_COLORS.length]
            // House placement = no specific venue OR private visibility.
            // The label "at a house" applies when they have *some* venue but
            // visibility is private; otherwise just "somewhere".
            const isHouse = s.visibility === 'private' && s.currentVenue != null
            const cityLabel = s.currentVenue?.city ?? 'somewhere private'
            return (
              <div
                key={s.nightId}
                data-testid={`anon-stumble-${s.ownerId}`}
                className="row-between"
                style={{
                  padding: '10px 14px',
                  border: '2.5px dashed var(--line)',
                  borderRadius: 14,
                  background: 'var(--paper-warm)',
                  textAlign: 'left',
                  minHeight: 44,
                }}
              >
                <div className="row" style={{ gap: 10 }}>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      background: color,
                      color: 'var(--paper)',
                      border: '2px solid var(--line)',
                      display: 'grid',
                      placeItems: 'center',
                      fontFamily: 'var(--font-display)',
                      fontSize: 14,
                    }}
                  >
                    {initialOf(s.ownerDisplayName)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 18,
                        lineHeight: 1,
                        textTransform: 'lowercase',
                      }}
                    >
                      {s.ownerDisplayName}
                    </div>
                    <div className="body-sm muted" style={{ fontSize: 11 }}>
                      {isHouse ? 'at a house' : 'somewhere'}
                      {cityLabel !== 'somewhere private' ? ` · ${cityLabel}` : ''}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 800 }}>
                    {s.drinkCount}
                  </div>
                  <div className="eyebrow" style={{ fontSize: 9 }}>
                    deep
                  </div>
                </div>
              </div>
            )
          })}
        </>
      )}
    </>
  )
}

// ── Proof tab ────────────────────────────────────────────────────────────
// Reads from /api/stumbling/proof — recent selfies from self + accepted friends
// on active nights, server-filtered for visibility (private nights excluded
// unless self). Auto-refetches on the `stumble:selfie-saved` window event so
// just-uploaded selfies appear without waiting for the 10s poll.

function ProofTab({
  proofs,
  loading,
  activeNightId,
  onOpenCamera,
}: {
  proofs: ProofItemDto[]
  loading: boolean
  activeNightId: string | null
  onOpenCamera: () => void
}) {
  if (loading && proofs.length === 0) {
    return (
      <div className="card body-sm muted" style={{ textAlign: 'center' }}>
        loading…
      </div>
    )
  }

  if (proofs.length === 0) {
    return (
      <div
        className="card"
        data-testid="proof-empty"
        style={{ textAlign: 'center', padding: 20, display: 'grid', gap: 8 }}
      >
        <p className="display" style={{ fontSize: 26, margin: 0 }}>
          No proof yet.
        </p>
        <p className="body-sm muted" style={{ fontSize: 13, margin: 0 }}>
          Snap a drink to start the polaroid wall.
        </p>
        <button
          type="button"
          className="chip"
          onClick={onOpenCamera}
          style={{ margin: '4px auto 0', minHeight: 36 }}
        >
          <Icon name="camera" size={14} /> {activeNightId ? 'open camera' : 'start a stumble'}
        </button>
      </div>
    )
  }

  // 3-col grid of polaroids, each captioned with venue + relative time.
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
        paddingBottom: 12,
      }}
    >
      {proofs.map((p, i) => {
        const color = AVATAR_COLORS[i % AVATAR_COLORS.length] ?? 'var(--lime)'
        const tilt = i % 2 === 0 ? 'tilt-l' : 'tilt-r'
        // publicUrl wins; dataUrl is the dev fallback. Both are safe to drop
        // into <img src>; storage-key proxies (legacy) already resolved to
        // /api/media/<key> server-side.
        const src = p.photoUrl ?? p.dataUrl ?? null
        const captureLabel = formatTimeAgo(p.capturedAt)
        // F-wave PR γ (F10): warm-ended polaroids fade so the Proof grid still
        // shows the just-wrapped friend's last selfie without it competing
        // visually with currently-active stumbles.
        const isWarmEnded = p.status === 'ended' && p.endedAt != null
        const endedLabel = isWarmEnded
          ? `ended ${formatEndedAgo(p.endedAt ?? new Date().toISOString())}`
          : null
        return (
          <div
            key={p.mediaId}
            className={`polaroid ${tilt}`}
            data-testid={`proof-card-${p.mediaId}`}
            data-warm={isWarmEnded ? 'true' : undefined}
            style={{
              opacity: isWarmEnded ? 0.7 : 1,
              filter: isWarmEnded ? 'grayscale(0.35)' : 'none',
            }}
          >
            <div
              className="photo"
              style={{
                background: color,
                display: 'grid',
                placeItems: 'center',
                color: 'var(--paper)',
                fontFamily: 'var(--font-display)',
                fontSize: 36,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {src ? (
                <img
                  src={src}
                  alt={`selfie by ${p.ownerDisplayName}${p.venueName ? ` at ${p.venueName}` : ''}`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                initialOf(p.ownerDisplayName)
              )}
            </div>
            <div className="cap" style={{ fontSize: 11 }}>
              {p.venueName ?? p.ownerDisplayName} · {endedLabel ?? captureLabel}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function formatTimeAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return 'just now'
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (diffSec < 60) return 'just now'
  const min = Math.round(diffSec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h`
  const d = Math.round(hr / 24)
  return `${d}d`
}

// F-wave PR γ (F10): grammar-friendly variant for "ended Xm ago" captions —
// never returns "just now" so the surrounding label stays well-formed at
// sub-minute resolution (warm-ended nights ≤ 1hr only).
function formatEndedAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return 'just now'
  const diffSec = Math.max(1, Math.round((Date.now() - t) / 1000))
  if (diffSec < 60) return `${diffSec}s ago`
  const min = Math.round(diffSec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  return `${hr}h ago`
}

// ── Squad tab ────────────────────────────────────────────────────────────
// Squad H2H is a post-MVP feature (tracked separately) — placeholder only.

function SquadTab() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 22, display: 'grid', gap: 10 }}>
      <p className="display" style={{ fontSize: 30, color: 'var(--ink)' }}>
        no squad battles tonight
      </p>
      <p className="body-sm muted" style={{ fontSize: 13 }}>
        Squads pit your crew vs another at every bar. Start one and tag tonight's stops.
      </p>
      <button
        type="button"
        className="btn pink full"
        style={{ marginTop: 4 }}
        onClick={() => {
          // /squad/setup ships in a follow-up — leave as a no-op for now so
          // the click feedback still fires on tap.
        }}
        disabled
        aria-disabled="true"
      >
        start a squad · coming soon
      </button>
    </div>
  )
}

// ── helpers ──────────────────────────────────────────────────────────────

function splitVisibility(stumbles: ActiveStumbleDto[]): {
  sharing: ActiveStumbleDto[]
  anon: ActiveStumbleDto[]
} {
  const sharing: ActiveStumbleDto[] = []
  const anon: ActiveStumbleDto[] = []
  for (const s of stumbles) {
    // PRIVATE bucket: visibility=private OR no current venue (houses don't
    // pin per ADR / chat-1 spec — "Houses don't pin").
    const isPrivate = s.visibility === 'private' || s.currentVenue == null
    if (isPrivate) anon.push(s)
    else sharing.push(s)
  }
  return { sharing, anon }
}

// Deterministic pseudo-position inside the map canvas (10–90% on both axes)
// from a string seed. Stable across polls so pins don't dance around.
function pseudoPosition(seed: string): { x: number; y: number } {
  let h = 5381
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) + h + seed.charCodeAt(i)) >>> 0
  }
  // Skew off-center a bit so we don't sit on top of the "you" dot.
  const x = 14 + (h % 72)
  const y = 16 + ((h >>> 7) % 64)
  return { x, y }
}

function initialOf(name: string): string {
  const trimmed = (name ?? '').trim()
  return trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?'
}

// Silence unused-warning for the row type — kept exported-shape style for
// future use when /api/stumbling/proof joins (carries the same shape).
export type { PositionedRow }
